// Private rooms (contracts/phase4.md §C, "Admin": GET /rooms?q): search by code, room id or player,
// with members and finished counts. The detail (GET /rooms/:code, "Phase 4 follow-ups") shows the room
// (code, link, type, window, week, set) and its board as admins see it: rank, handle, orb, progress,
// score, total solving time, when each finished and any anti-cheat flags. Rooms are never rated.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { RoomStatusFilter } from "../api/client";
import { useRoom, useRooms } from "../api/queries";
import type { RoomRow } from "../api/schemas";
import { Orb } from "../components/Orb";
import { tableStyles } from "../components/table";
import { Card, Chip, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Skeleton, Stat, TextInput } from "../components/ui";
import { WEB_ORIGIN } from "../env";
import { formatDateTime } from "../lib/dates";
import { WINDOW_LABEL } from "../lib/duels";
import { fmtInt, fmtMs, shortId } from "../lib/format";
import d from "./duels.module.css";
import styles from "./pages.module.css";

const who = (p: { name: string | null; handle: string | null; userId: string | null } | null) =>
  p ? (p.handle ? `@${p.handle}` : (p.name ?? (p.userId ? shortId(p.userId) : "—"))) : "—";

export function RoomsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const raw = params.get("status");
  const status: RoomStatusFilter = raw === "open" || raw === "closed" ? raw : null;
  const rooms = useRooms(q, status);
  const navigate = useNavigate();

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (draft === q) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (draft) next.set("q", draft);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(id);
  }, [draft, q, setParams]);

  const items = rooms.data?.items ?? [];
  return (
    <>
      <PageHeader title="Rooms" meta={<span>Private rooms of up to 8, newest first. Everyone in a room plays the same set; rooms are never rated.</span>} />
      <div className={styles.filters}>
        <Field label="Search">
          <TextInput data-search value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="K4P9, a player, a room id…   ( / )" style={{ width: 320 }} autoFocus />
        </Field>
        <Segmented
          label="Status"
          value={status ?? "all"}
          onChange={(v) =>
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") next.delete("status");
                else next.set("status", v);
                return next;
              },
              { replace: true },
            )
          }
          options={[
            { value: "all", label: "All" },
            { value: "open", label: "Open" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <span className={styles.filtersEnd}>
          <span className="monoLabel">{rooms.data ? `${items.length} room${items.length === 1 ? "" : "s"}` : ""}</span>
        </span>
      </div>
      {rooms.isError ? (
        <ErrorState error={rooms.error} onRetry={() => void rooms.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={rooms.isFetching}>
          <table className={tableStyles.table} data-testid="rooms">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Window</th>
                <th>Creator</th>
                <th>Members</th>
                <th data-align="right">Finished</th>
                <th data-align="right">Invited</th>
                <th>Created</th>
                <th>Closes</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rooms.isPending
                ? Array.from({ length: 10 }, (_, i) => (
                    <tr key={i}>
                      {[50, 60, 40, 110, 80, 30, 30, 90, 90, 50].map((w, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={w} />
                        </td>
                      ))}
                    </tr>
                  ))
                : items.map((r) => <RoomListRow key={r.id} room={r} onOpen={() => navigate(`/rooms/${encodeURIComponent(r.id)}`)} />)}
            </tbody>
          </table>
          {!rooms.isPending && items.length === 0 ? (
            <EmptyState title={q ? "No rooms found" : "No rooms yet"}>{q ? `Nothing matches “${q}”.` : "Rooms players open from Duels show up here."}</EmptyState>
          ) : null}
        </div>
      )}
    </>
  );
}

function RoomListRow({ room: r, onOpen }: { room: RoomRow; onOpen: () => void }) {
  return (
    <tr data-clickable data-room={r.id} data-status={r.status} onClick={(e) => !(e.target as HTMLElement).closest("a") && onOpen()}>
      <td>
        <Link to={`/rooms/${encodeURIComponent(r.id)}`} className={d.code}>
          {r.code}
        </Link>
        {r.rematchOf ? (
          <span className={tableStyles.faint} title={`Rematch of ${r.rematchOf}`}>
            {" "}
            ↻
          </span>
        ) : null}
      </td>
      <td>{r.type}</td>
      <td className={tableStyles.mono}>{r.window ? WINDOW_LABEL[r.window] : "—"}</td>
      <td>
        {r.creator?.userId ? (
          <Link to={`/players/${r.creator.userId}`} className={`${tableStyles.id} ${tableStyles.dim}`}>
            {who(r.creator)}
          </Link>
        ) : (
          <span className={tableStyles.dim}>{who(r.creator)}</span>
        )}
      </td>
      <td>
        <span className={d.fill}>
          <span className={styles.meter}>
            <span style={{ width: `${Math.min(100, (r.memberCount / Math.max(1, r.cap)) * 100)}%` }} />
          </span>
          <span className="mono">
            {r.memberCount}/{r.cap}
          </span>
        </span>
      </td>
      <td data-align="right" className="mono" data-testid="room-finished">
        {r.finished === null ? <span className={tableStyles.faint}>—</span> : r.finished ? r.finished : <span className={tableStyles.faint}>0</span>}
      </td>
      <td data-align="right" className="mono">
        {r.invited ? r.invited : <span className={tableStyles.faint}>{r.invited === null ? "—" : 0}</span>}
      </td>
      <td className={tableStyles.mono}>{r.createdAt ? formatDateTime(r.createdAt).replace(" UTC", "") : "—"}</td>
      <td className={tableStyles.mono}>{r.closesAt ? formatDateTime(r.closesAt).replace(" UTC", "") : "—"}</td>
      <td>
        <Chip tone={r.status === "open" ? "success" : "muted"}>{r.status}</Chip>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- detail

export function RoomDetailPage() {
  const { id = "" } = useParams();
  const q = useRoom(id);
  const room = q.data?.room;
  const header = (
    <PageHeader
      title={room ? <span className={d.title}>Room {room.code}</span> : <span className="mono">{shortId(id, 20)}</span>}
      meta={
        <>
          <Link to="/rooms" className={styles.back}>
            ← Rooms
          </Link>
          {room ? (
            <>
              <Chip tone={room.status === "open" ? "success" : "muted"}>{room.status}</Chip>
              <Chip tone="neutral">{room.type}</Chip>
              <Chip tone="muted">never rated</Chip>
            </>
          ) : null}
          <span className="mono">{id}</span>
        </>
      }
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such room" : "Couldn't load this room"} />
      </>
    );
  const { room: r, members } = q.data;
  const link = r.url ?? `${WEB_ORIGIN}/r/${r.code}`;
  const scores = members.map((m) => m.score).filter((v): v is number => v !== null);
  const best = scores.length ? Math.max(...scores) : null;
  return (
    <>
      {header}
      <div className={d.roomTop}>
        <Card title="Room">
          <dl className={styles.kv} data-testid="room-profile">
            <dt>Code</dt>
            <dd className="mono">{r.code}</dd>
            <dt>Link</dt>
            <dd className="mono">
              <a href={link} target="_blank" rel="noreferrer" className={styles.back}>
                {link.replace(/^https?:\/\//, "")}
              </a>
            </dd>
            <dt>Type</dt>
            <dd>{r.type}</dd>
            <dt>Window</dt>
            <dd>{r.window ? (r.window === "24h" ? "24 hours" : "a week") : "—"}</dd>
            <dt>Creator</dt>
            <dd>{r.creator?.userId ? <Link to={`/players/${r.creator.userId}`}>{who(r.creator)}</Link> : who(r.creator)}</dd>
            <dt>Created</dt>
            <dd className="mono">{r.createdAt ? formatDateTime(r.createdAt) : "—"}</dd>
            <dt>{r.status === "open" ? "Closes" : "Closed"}</dt>
            <dd className="mono">{r.closedAt ? formatDateTime(r.closedAt) : r.closesAt ? formatDateTime(r.closesAt) : "—"}</dd>
            <dt>Set</dt>
            <dd className="mono">
              {r.setId ?? "—"}
              {r.week ? <span className={styles.faint}> · {r.week}</span> : null}
            </dd>
            {r.rematchOf ? (
              <>
                <dt>Rematch of</dt>
                <dd className="mono">
                  <Link to={`/rooms/${encodeURIComponent(r.rematchOf)}`}>{shortId(r.rematchOf, 16)}</Link>
                </dd>
              </>
            ) : null}
            {r.rematchId ? (
              <>
                <dt>Rematch</dt>
                <dd className="mono">
                  <Link to={`/rooms/${encodeURIComponent(r.rematchId)}`}>{shortId(r.rematchId, 16)}</Link>
                </dd>
              </>
            ) : null}
          </dl>
        </Card>
        <Card title="So far">
          <div className={styles.statsRow}>
            <Stat label="Members" value={`${r.memberCount} / ${r.cap}`} sub={r.memberCount >= r.cap ? "full" : `${r.cap - r.memberCount} open`} />
            <Stat label="Finished" value={fmtInt(r.finished ?? (members.length ? members.filter((m) => m.finished).length : null))} sub={`of ${r.memberCount}`} />
            <Stat label="Best" value={fmtInt(best)} sub="set score" />
          </div>
        </Card>
      </div>
      <Card title="Board" padded={false} actions={<Chip tone="muted">{`${Math.max(members.length, r.memberCount)} / ${r.cap}`}</Chip>}>
        {members.length === 0 ? (
          r.memberCount > 0 ? (
            <EmptyState title={`${r.memberCount} joined`}>The API gives this room's head count but not its members, so there's no board to show.</EmptyState>
          ) : (
            <EmptyState title="No members">Nobody has joined this room.</EmptyState>
          )
        ) : (
          <table className={tableStyles.table} data-testid="room-members">
            <thead>
              <tr>
                <th data-align="right">Rank</th>
                <th>Player</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Set</th>
                <th data-align="right">Score</th>
                <th data-align="right">Time</th>
                <th>Finished</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.userId ?? i} data-finished={m.finished || undefined} data-state={m.state ?? undefined}>
                  <td data-align="right" className="mono" data-testid="room-rank">
                    {m.rank ?? (members.some((x) => x.rank !== null) ? "—" : m.finished && m.score !== null ? i + 1 : "—")}
                  </td>
                  <td>
                    {m.userId ? (
                      <Link to={`/players/${m.userId}`} className={d.side}>
                        {m.orb !== null ? <Orb index={m.orb} size={18} /> : null}
                        <span className={d.sideName}>{who(m)}</span>
                      </Link>
                    ) : (
                      <span className={d.side}>{who(m)}</span>
                    )}
                  </td>
                  <td>
                    {m.role === "creator" || m.role === "host" ? <Chip tone="accent">creator</Chip> : <span className={tableStyles.dim}>member</span>}
                    {m.state === "invited" ? (
                      <>
                        {" "}
                        <Chip tone="muted">invited</Chip>
                      </>
                    ) : null}
                  </td>
                  <td className={tableStyles.mono}>{m.joinedAt ? formatDateTime(m.joinedAt) : m.invitedAt ? <span className={tableStyles.faint}>invited {formatDateTime(m.invitedAt)}</span> : "—"}</td>
                  <td>
                    <span className={d.fill}>
                      <span className={styles.meter}>
                        <span style={{ width: `${((m.played ?? (m.finished ? 10 : 0)) / 10) * 100}%` }} />
                      </span>
                      <span className="mono">{m.played === null ? (m.finished ? "done" : "—") : `${m.played}/10`}</span>
                    </span>
                  </td>
                  <td data-align="right" className="mono">
                    {m.score === null ? <span className={tableStyles.faint}>—</span> : fmtInt(m.score)}
                  </td>
                  <td data-align="right" className="mono">
                    {m.timeMs ? fmtMs(m.timeMs) : <span className={tableStyles.faint}>—</span>}
                  </td>
                  <td className={tableStyles.mono}>{m.finishedAt ? formatDateTime(m.finishedAt) : m.finished ? "yes" : <span className={tableStyles.faint}>not yet</span>}</td>
                  <td>{m.flags.length ? m.flags.map((f) => <Chip key={f} tone="warning">{f.replace(/_/g, " ")}</Chip>) : <span className={tableStyles.faint}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
